import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import {
  deleteIngredientGroup,
  ingredientGroupQuery,
  ingredientGroupsQuery,
  updateIngredientGroup,
} from '@/api'
import { languagesQuery } from '@/api/languages'
import type { IngredientGroup } from '@/api/types'
import EditIngredientGroup from '@/features/ingredients/EditIngredientGroup'

export const Route = createFileRoute('/ingredients_/displaynames_/$ingId')({
  component: Component,
  loader: async ({ context: { queryClient }, params }) => {
    const [ingredientGroup, languages] = await Promise.all([
      queryClient.fetchQuery(ingredientGroupQuery(parseInt(params.ingId))),
      queryClient.ensureQueryData(languagesQuery),
    ])

    return { ingredientGroup, languages }
  },
})

function Component() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { ingredientGroup, languages } = Route.useLoaderData()
  const queryClient = useQueryClient()

  const updateMutation = useMutation({
    mutationFn: (group: IngredientGroup) => {
      return updateIngredientGroup(group.idIngredientGroup ?? 0, {
        name: group.name,
        ingredientIds: group.ingredients.map((ig) => ig.idIngredient),
        nameTranslations: group.nameTranslations,
      })
    },
    onSuccess: () => {
      toast.success(t('savedSuccess'))
    },
  })

  const handleSaveGroup = (group: IngredientGroup) => {
    updateMutation.mutate(group)
  }

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteIngredientGroup(id),
    onSuccess: (res, id) => {
      queryClient.setQueryData(ingredientGroupsQuery.queryKey, (oldData) => {
        if (oldData) {
          return oldData.filter((item) => item.idIngredientGroup !== id)
        }
      })

      navigate({
        to: '/ingredients',
      })
    },
  })

  const handleDeleteGroup = (id: number) => {
    deleteMutation.mutate(id)
  }

  return (
    <EditIngredientGroup
      ingredientGroup={ingredientGroup}
      languages={languages}
      deleteGroup={handleDeleteGroup}
      saveGroup={handleSaveGroup}
    />
  )
}
